import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import ExportToExcelButton from '@components/ui/ExportToExcelButton';
import PageLayout from '@components/layout/PageLayout';
import styles from './SppReportsAccountingPage.module.css';
import { useSppReportsAccountingPage } from './useSppReportsAccountingPage';
import useSession from '@hooks/useSession';

// Type for the table row data
type SPPReportRow = {
  meter_id: string | number;
  company: string;
  location: string;
  meter_number: string;
  generation_type: string;
  jan?: number | null;
  feb?: number | null;
  mar?: number | null;
  apr?: number | null;
  may?: number | null;
  jun?: number | null;
  jul?: number | null;
  aug?: number | null;
  sep?: number | null;
  oct?: number | null;
  nov?: number | null;
  dec?: number | null;
  ytd_total: number;
};

const SppReportsAccountingPage = () => {
  const { currentYear, navigateYear, isNextYearDisabled, data, loading, error, handleExportToExcel } = useSppReportsAccountingPage();
  const { isUser, isManager, isAdmin, isSuperAdmin } = useSession();
  const canExport = isUser || isManager || isAdmin || isSuperAdmin;

  if (error) {
    return (
      <PageLayout title="SPP - Accounting Report (Pre Tax)">
        <div className={styles.content}>
          <p>Error loading data: {error.message}</p>
        </div>
      </PageLayout>
    );
  }

  // Format currency
  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '—';
    return `$${value.toLocaleString('en-CA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Define columns
  const columns: ColumnsType<SPPReportRow> = [
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      width: 120,
      fixed: 'left',
      ellipsis: true,
    },
    {
      title: 'Name',
      dataIndex: 'location',
      key: 'location',
      width: 150,
      fixed: 'left',
      ellipsis: true,
    },
    {
      title: 'Meter Number',
      dataIndex: 'meter_number',
      key: 'meter_number',
      width: 130,
      fixed: 'left',
      render: (value: string, record) => (
        <Link to={`/meterreaddetails/${record.meter_id}`}>{value}</Link>
      ),
    },
    {
      title: 'Generation Type',
      dataIndex: 'generation_type',
      key: 'generation_type',
      width: 130,
      fixed: 'left',
    },
    {
      title: 'Jan',
      dataIndex: 'jan',
      key: 'jan',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Feb',
      dataIndex: 'feb',
      key: 'feb',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Mar',
      dataIndex: 'mar',
      key: 'mar',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Apr',
      dataIndex: 'apr',
      key: 'apr',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'May',
      dataIndex: 'may',
      key: 'may',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Jun',
      dataIndex: 'jun',
      key: 'jun',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Jul',
      dataIndex: 'jul',
      key: 'jul',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Aug',
      dataIndex: 'aug',
      key: 'aug',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Sep',
      dataIndex: 'sep',
      key: 'sep',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Oct',
      dataIndex: 'oct',
      key: 'oct',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Nov',
      dataIndex: 'nov',
      key: 'nov',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Dec',
      dataIndex: 'dec',
      key: 'dec',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'YTD Total',
      dataIndex: 'ytd_total',
      key: 'ytd_total',
      width: 140,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
  ];

  // Render a single table for a program type
  const renderTable = (title: string, dataSource: SPPReportRow[], tableKey: string) => (
    <div key={tableKey} className={styles.tableSection}>
      <h2 className={styles.tableTitle}>{title}</h2>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey="meter_id"
        pagination={false}
        scroll={{ x: 'max-content', y: 400 }}
        bordered
        size="small"
        locale={{ emptyText: `No ${title} meters found for this period` }}
      />
    </div>
  );

  return (
    <PageLayout
      title="SPP - Accounting Report (Pre Tax)"
      headerAccessoryView={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {canExport && (
            <ExportToExcelButton
              onClick={handleExportToExcel}
              disabled={loading}
            />
          )}
          <div className={styles.yearNavigation}>
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={() => navigateYear(-1)}
              className={styles.navBtn}
            />
            <span className={styles.yearLabel}>
              {currentYear}
            </span>
            <Button
              type="text"
              icon={<RightOutlined />}
              onClick={() => navigateYear(1)}
              disabled={isNextYearDisabled}
              className={styles.navBtn}
            />
          </div>
        </div>
      }
    >
      <div className={styles.content}>
        {renderTable('IPP', data.ippMeters, 'ipp')}
        {renderTable('COMFIT', data.comfitMeters, 'comfit')}
        {renderTable('AGC Wind Farm', data.maxcapMeters, 'maxcap')}
        {renderTable('Community Solar', data.communitySolarMeters, 'community-solar')}
      </div>
    </PageLayout>
  );
};

export default SppReportsAccountingPage;
